import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase/config';

/**
 * namat#130: the Authentica and Evolution API keys live in the server's Secret
 * Manager (AUTHENTICA_API_KEY / EVOLUTION_API_KEY), not in Firestore. The admin
 * settings screen only learns whether each key is configured and where from —
 * `messagingProviderStatus` never returns a key.
 */
export type KeySource = 'secret' | 'firestore' | null;

export interface MessagingProviderStatus {
  authentica: { configured: boolean; source: KeySource };
  evolution: { keyConfigured: boolean; source: KeySource; configured: boolean };
}

export async function getMessagingProviderStatus(): Promise<MessagingProviderStatus | null> {
  try {
    const call = httpsCallable<Record<string, never>, MessagingProviderStatus>(
      functions,
      'messagingProviderStatus'
    );
    const { data } = await call({});
    return data;
  } catch (e) {
    console.warn('Could not load messaging provider status:', e);
    return null;
  }
}

/**
 * The one line the admin sees in place of the old key input.
 * `null` status = the callable failed (e.g. not deployed yet), so say nothing
 * definite.
 */
export function keyStatusText(
  status: { source: KeySource } | null | undefined,
  isRTL: boolean
): string {
  if (!status) {
    return isRTL ? 'تعذّر التحقق من حالة المفتاح' : 'Could not check the key status';
  }
  if (status.source === 'secret') {
    return isRTL
      ? 'المفتاح مُعدّ في أسرار الخادم (Secret Manager)'
      : 'Configured in server secrets (Secret Manager)';
  }
  if (status.source === 'firestore') {
    return isRTL
      ? 'المفتاح ما زال في Firestore (مرحلة انتقالية) — يجب نقله إلى أسرار الخادم'
      : 'Still read from Firestore (transition) — move it to server secrets';
  }
  return isRTL
    ? 'غير مُعدّ — يُضبط في أسرار الخادم (Secret Manager)'
    : 'Not configured — set it in server secrets (Secret Manager)';
}
